/**
 * RegimeDiffChart — 5d smoothed vs Instant HMM Viterbi over the last N bars.
 *
 * Closes the loop on the "why did AAPL flip Volatile Instant while 5d stayed
 * Bull?" question — user request from iter126. Divergence periods are shaded
 * amber to match the existing `pi-regime-disagreement` chip.
 *
 * Backend contract:
 *   GET /api/regime/diff/{ticker}?days=60
 *   → { rows: [{ date, state_instant, state_5d, price, disagree }],
 *       disagree_bars, disagree_pct }
 */
import { useEffect, useMemo, useState } from "react";
import {
  ComposedChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceArea,
} from "recharts";
import api from "@/lib/api";

// State → numeric lane so we can render two step-lines on one Y-axis.
// Ordered top→bottom by "market-friendliness for premium sellers": Bull
// at the top (calm long-side), Quiet Range next, Bearish, then Volatile
// at the bottom (least premium-friendly).
const STATE_LANES = {
  "Bullish Trend": 3,
  "Quiet Range":   2,
  "Bearish Trend": 1,
  "Volatile":      0,
};
const LANE_LABELS = { 3: "Bull", 2: "Quiet", 1: "Bear", 0: "Volatile" };
const stateColor = {
  "Bullish Trend": "#10B981",
  "Quiet Range":   "#3B82F6",
  "Bearish Trend": "#EF4444",
  "Volatile":      "#F59E0B",
};

export default function RegimeDiffChart({ symbol, days = 60, intradayHint = null, hideFrame = false }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!symbol) return;
    let cancelled = false;
    setData(null); setErr(null);
    api.get(`/regime/diff/${symbol}?days=${days}`)
      .then(r => { if (!cancelled) setData(r.data); })
      .catch(e => { if (!cancelled) setErr(e?.response?.data?.detail || e?.message); });
    return () => { cancelled = true; };
  }, [symbol, days]);

  // Precompute lane values + contiguous divergence bands for ReferenceArea shading.
  const { chart, bands } = useMemo(() => {
    if (!data?.rows?.length) return { chart: [], bands: [] };
    const chart = data.rows.map(r => ({
      date: r.date,
      instant: STATE_LANES[r.state_instant] ?? null,
      smoothed: STATE_LANES[r.state_5d] ?? null,
      instantLabel: r.state_instant,
      smoothedLabel: r.state_5d,
      disagree: r.disagree,
      price: r.price,
    }));
    // Collapse runs of consecutive `disagree` bars into ReferenceArea bands.
    const bands = [];
    let cur = null;
    for (const row of chart) {
      if (row.disagree) {
        if (!cur) cur = { x1: row.date, x2: row.date };
        else cur.x2 = row.date;
      } else if (cur) {
        bands.push(cur); cur = null;
      }
    }
    if (cur) bands.push(cur);
    return { chart, bands };
  }, [data]);

  if (err) {
    return (
      <div data-testid="regime-diff-chart-error" className="text-[10px] px-3 py-2"
           style={{ color: "var(--hoip-text-tertiary)" }}>
        Regime diff unavailable: {String(err).slice(0, 100)}
      </div>
    );
  }
  if (!data) {
    return (
      <div data-testid="regime-diff-chart-loading" className="text-[10px] px-3 py-2"
           style={{ color: "var(--hoip-text-tertiary)" }}>
        Loading regime diff…
      </div>
    );
  }
  if (!chart.length) {
    return (
      <div data-testid="regime-diff-chart-empty" className="text-[10px] px-3 py-2"
           style={{ color: "var(--hoip-text-tertiary)" }}>
        No regime history yet for {symbol}. Diff chart populates after the daily scheduler run.
      </div>
    );
  }

  return (
    <div data-testid="regime-diff-chart" className={hideFrame ? "" : "border p-3"}
         style={hideFrame ? undefined : { background: "var(--hoip-card)", borderColor: "var(--hoip-border)" }}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-[11px] font-bold" style={{ color: "var(--hoip-text-primary)" }}>
          Regime Diff — 5d smoothed vs Instant
        </div>
        <div className="text-[9px] flex items-center gap-2" style={{ color: "var(--hoip-text-tertiary)" }}>
          {data.pending_today && (
            <span className="px-1.5 py-0.5 border rounded-sm" data-testid="regime-diff-pending-today"
              style={{ color: "#F59E0B", borderColor: "rgba(245,158,11,0.4)", background: "rgba(245,158,11,0.08)" }}
              title="The HMM regime for the newest bar is computed after the daily close; today fills in once the bar completes. The intraday lean is a lightweight technical read (trend/RSI), not the HMM output.">
              Today: pending — updates after close{intradayHint ? ` · ${intradayHint}` : ""}
            </span>
          )}
          <span>
            <span className="font-mono-data" style={{ color: "#F59E0B" }}>
              {data.disagree_pct}%
            </span>
            {" "}disagreement over last {data.days} bars
          </span>
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-3 mb-2 text-[9px]" style={{ color: "var(--hoip-text-secondary)" }}>
        <span className="flex items-center gap-1">
          <span className="inline-block w-3 h-0.5" style={{ background: "#10B981" }} />
          <span>5d smoothed (primary driver)</span>
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block w-3 h-0.5" style={{ background: "#F59E0B", opacity: 0.7 }} />
          <span>Instant (event context)</span>
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block w-3 h-3" style={{ background: "rgba(245,158,11,0.18)" }} />
          <span>Divergence</span>
        </span>
      </div>

      <div style={{ width: "100%", height: 180 }}>
        <ResponsiveContainer>
          <ComposedChart data={chart} margin={{ top: 6, right: 12, left: 0, bottom: 4 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--hoip-border)" opacity={0.35} />
            <XAxis dataKey="date" tick={{ fill: "var(--hoip-text-tertiary)", fontSize: 9 }}
                   tickFormatter={(d) => d?.slice(5)}
                   interval="preserveStartEnd" />
            <YAxis domain={[-0.4, 3.4]} ticks={[0, 1, 2, 3]}
                   tickFormatter={(v) => LANE_LABELS[v] || ""}
                   tick={{ fill: "var(--hoip-text-tertiary)", fontSize: 9 }} width={54} />
            {/* Amber divergence bands */}
            {bands.map((b, i) => (
              <ReferenceArea key={i} x1={b.x1} x2={b.x2} y1={-0.4} y2={3.4}
                             fill="#F59E0B" fillOpacity={0.13}
                             stroke="none" ifOverflow="visible" />
            ))}
            <Tooltip
              contentStyle={{ background: "var(--hoip-card)", border: "1px solid var(--hoip-border)", fontSize: 10 }}
              labelStyle={{ color: "var(--hoip-text-secondary)" }}
              formatter={(_v, name, entry) => {
                const p = entry?.payload || {};
                if (name === "smoothed") return [p.smoothedLabel, "5d smoothed"];
                if (name === "instant")  return [p.instantLabel,  "Instant"];
                return null;
              }}
            />
            <Line type="stepAfter" dataKey="smoothed" stroke="#10B981" strokeWidth={2}
                  dot={false} isAnimationActive={false} name="smoothed" />
            <Line type="stepAfter" dataKey="instant" stroke="#F59E0B" strokeWidth={1.5}
                  strokeDasharray="4 2" dot={false} isAnimationActive={false} name="instant" opacity={0.85} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {/* Amber footnote pinned to the same spec that drives the haircut. */}
      <div className="text-[9px] mt-2" style={{ color: "var(--hoip-text-tertiary)" }}>
        Ranked strategies use the <span style={{ color: stateColor["Bullish Trend"] }}>5d smoothed</span> path;
        Instant is context only. Amber bands mark bars where the two disagree — see the disagreement chip on the header.
      </div>
    </div>
  );
}
